import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { pushApi } from '@/lib/api';
import {
  disablePushNotifications,
  enablePushNotifications,
  getNotificationPermission,
  getPushSetupStatus,
  isAppleTouchDevice,
  isPushSupported,
  type PushSetupStatus,
} from '@/lib/push';
import { isStandaloneDisplay } from '@/lib/pwa';
import { toast } from 'sonner';

type PushPrefs = {
  pushInvitationsEnabled: boolean;
  pushSharedSpendingEnabled: boolean;
  hasSubscription: boolean;
};

function guidanceForStatus(status: PushSetupStatus, appleDevice: boolean): {
  title: string;
  body: string;
  steps?: string[];
} {
  switch (status) {
    case 'needs_home_screen':
      if (appleDevice) {
        return {
          title: 'Install My Budget to enable notifications',
          body: 'On iPhone and iPad, push is only available after you add the app to your Home Screen and open it from that icon — not from a Safari tab.',
          steps: [
            'In Safari, tap Share',
            'Choose Add to Home Screen, then Add',
            'Open My Budget from the Home Screen icon',
            'Return here and tap Enable and send test',
          ],
        };
      }
      return {
        title: 'Install My Budget to enable notifications',
        body: 'Open My Budget as an installed app (Home Screen / installed window), then you can turn on push for budget invitations and shared spending on this device.',
        steps: [
          'Use your browser’s Install app or Add to Home Screen',
          'Open My Budget from the installed app icon',
          'Return here and tap Enable and send test',
        ],
      };
    case 'permission_denied':
      return {
        title: 'Notifications are blocked for My Budget',
        body: appleDevice
          ? 'This app cannot ask again. On your iPhone, open Settings → Notifications → My Budget and allow notifications, then return here and tap Enable and send test.'
          : 'This app cannot ask again. Allow notifications for My Budget in your device or browser settings, then return here and tap Enable and send test.',
      };
    case 'unsupported':
      return {
        title: 'Push is not available on this device',
        body: 'This browser does not expose Web Push (service worker / PushManager / Notification). Try a current Safari or Chrome build, or open the installed Home Screen app on iOS 16.4+.',
      };
    default:
      return {
        title: '',
        body: '',
      };
  }
}

export function NotificationsSettingsCard() {
  const [pushSupported] = React.useState(() => isPushSupported());
  const [isStandalone, setIsStandalone] = React.useState(isStandaloneDisplay);
  const [permission, setPermission] = React.useState(getNotificationPermission);
  const [appleDevice] = React.useState(() => isAppleTouchDevice());
  const [pushBusy, setPushBusy] = React.useState(false);
  const [pushPrefs, setPushPrefs] = React.useState<PushPrefs | null>(null);

  const status = getPushSetupStatus({
    isStandalone,
    pushSupported,
    permission,
  });
  const canManage = status === 'ready';

  React.useEffect(() => {
    const sync = () => {
      setIsStandalone(isStandaloneDisplay());
      setPermission(getNotificationPermission());
    };
    sync();
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      mq.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  React.useEffect(() => {
    if (!canManage) {
      setPushPrefs(null);
      return;
    }
    let cancelled = false;
    pushApi
      .getPreferences()
      .then((prefs) => {
        if (!cancelled) setPushPrefs(prefs);
      })
      .catch(() => {
        if (!cancelled) setPushPrefs(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const refreshPushPrefs = async () => {
    const prefs = await pushApi.getPreferences();
    setPushPrefs(prefs);
    setPermission(getNotificationPermission());
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      const ok = await enablePushNotifications();
      setPermission(getNotificationPermission());
      if (ok) {
        toast.success('Test notification sent');
        await refreshPushPrefs();
      } else {
        toast('Notifications permission was not granted');
      }
    } catch (err) {
      setPermission(getNotificationPermission());
      toast.error(err instanceof Error ? err.message : 'Could not enable notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await disablePushNotifications();
      await refreshPushPrefs();
      toast.success('Notifications disabled on this device');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not disable notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleInvitationPref = async (enabled: boolean) => {
    setPushBusy(true);
    try {
      const prefs = await pushApi.updatePreferences({ pushInvitationsEnabled: enabled });
      setPushPrefs(prefs);
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setPushBusy(false);
    }
  };

  const handleSharedSpendingPref = async (enabled: boolean) => {
    setPushBusy(true);
    try {
      const prefs = await pushApi.updatePreferences({ pushSharedSpendingEnabled: enabled });
      setPushPrefs(prefs);
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setPushBusy(false);
    }
  };

  const guidance = guidanceForStatus(status, appleDevice);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManage ? (
          <>
            <p className="text-sm text-muted-foreground">
              Enable browser push on this device for budget invitations and shared spending.
              Available to every signed-in user in the installed app — not an admin setting.
              Invitation emails are always sent when mail is configured.
            </p>
            {pushPrefs?.hasSubscription ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleDisablePush}
                loading={pushBusy}
              >
                Disable on this device
              </Button>
            ) : (
              <Button className="w-full" onClick={handleEnablePush} loading={pushBusy}>
                Enable and send test
              </Button>
            )}
            {pushPrefs && (
              <div className="space-y-3">
                <label className="flex items-center justify-between gap-4 text-sm">
                  <span>Budget invitations</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={pushPrefs.pushInvitationsEnabled}
                    disabled={pushBusy}
                    onChange={(e) => handleInvitationPref(e.target.checked)}
                  />
                </label>
                <label className="flex items-center justify-between gap-4 text-sm">
                  <span>Shared spending</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={pushPrefs.pushSharedSpendingEnabled}
                    disabled={pushBusy}
                    onChange={(e) => handleSharedSpendingPref(e.target.checked)}
                  />
                </label>
              </div>
            )}
          </>
        ) : (
          <div
            className="rounded-md border border-border bg-muted/40 px-3 py-3 space-y-2"
            role="status"
            data-push-setup-status={status}
          >
            <p className="text-sm font-medium text-foreground">{guidance.title}</p>
            <p className="text-sm text-muted-foreground">{guidance.body}</p>
            {guidance.steps && guidance.steps.length > 0 && (
              <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground">
                {guidance.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
